import { Check, Landmark, Smartphone, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FieldWrapper } from "@/components/ui/FieldWrapper";
import Input from "@/components/ui/Input";
import DatePicker from "@/components/ui/DatePicker";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import type { AuthMode } from "@/types/mandate-form";
import { addMonthsToDate, computeEmi, formatMoney, isDateToday, roundUpToHundred } from "../emi";
import type { FormErrors, MandateForm, SetField } from "../types";
import PrefixInput from "./PrefixInput";

const MODES: { key: AuthMode; icon: LucideIcon; title: string; sub: string }[] = [
  {
    key: "NACH",
    icon: Landmark,
    title: "eNACH (Bank Account)",
    sub: "Debited from the customer's bank account. The customer chooses net banking, debit card or Aadhaar OTP on the authorisation page.",
  },
  {
    key: "UPI_AUTO_PAY",
    icon: Smartphone,
    title: "UPI Autopay",
    sub: "Approved by the customer in any UPI app. Fastest to activate.",
  },
];

const PRESET_TENURES = ["3", "6", "9", "12", "18", "24", "36"];

const TENURE_OPTIONS = [
  ...PRESET_TENURES.map((t) => ({
    value: t,
    label: `${t} months`,
  })),
  { value: "OTHER", label: "Other (Enter custom months)" },
];

const FREQUENCY_OPTIONS = [{ value: "Monthly", label: "Monthly" }];

const PURPOSE_OPTIONS = [{ value: "EMI Collection", label: "EMI Collection" }];

interface Props {
  form: MandateForm;
  errors: FormErrors;
  setField: SetField;
}

function DefRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="shrink-0 text-[13px] text-ink-secondary">{label}</span>
      <span className={`text-right text-sm font-medium ${muted ? "text-ink-muted" : "text-ink"}`}>
        {value}
      </span>
    </div>
  );
}

export default function EmiStep({ form, errors, setField }: Props) {
  const emi = computeEmi(
    form.productPrice,
    form.downPayment,
    form.interestRate,
    form.tenure,
  );

  const [isOtherTenure, setIsOtherTenure] = useState<boolean>(() => {
    return !!form.tenure && !PRESET_TENURES.includes(form.tenure);
  });

  useEffect(() => {
    if (form.tenure && !PRESET_TENURES.includes(form.tenure)) {
      setIsOtherTenure(true);
    }
  }, [form.tenure]);

  useEffect(() => {
    setField("frequency", "Monthly");
    setField("purpose", "EMI Collection");
    const r = computeEmi(form.productPrice, form.downPayment, form.interestRate, form.tenure);
    if (r) {
      setField("amount", r.emi.toFixed(2));
      setField("maxAmount", String(roundUpToHundred(r.emi)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.productPrice, form.downPayment, form.interestRate, form.tenure]);

  useEffect(() => {
    if (form.startDate && form.tenure) {
      setField("endDate", addMonthsToDate(form.startDate, Number(form.tenure)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startDate, form.tenure]);

  const emdash = "—";

  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">EMI &amp; Mandate Details</h3>
      <p className="text-[13px] text-ink-secondary">
        Financing terms and the recurring debit schedule
      </p>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <FieldWrapper
          label="Product Price"
          required
          error={errors.productPrice}
          htmlFor="productPrice"
        >
          <PrefixInput
            prefix="₹"
            id="productPrice"
            inputMode="numeric"
            placeholder="Enter product price"
            error={!!errors.productPrice}
            value={form.productPrice}
            onChange={(e) => setField("productPrice", e.target.value.replace(/[^\d.]/g, ""))}
          />
        </FieldWrapper>

        <FieldWrapper
          label="Down Payment / Advance Collection"
          required
          error={errors.downPayment}
          htmlFor="downPayment"
          helper={
            form.authMode === "NACH" && Number(form.downPayment || 0) > 0
              ? "eNACH does not support advance collection (must be ₹0)"
              : form.authMode === "UPI_AUTO_PAY"
                ? "NPCI UPI AutoPay limit: Max ₹15,000 advance"
                : "Amount paid upfront by the customer"
          }
        >
          <PrefixInput
            prefix="₹"
            id="downPayment"
            inputMode="numeric"
            placeholder="Enter down payment (0 if none)"
            error={!!errors.downPayment}
            value={form.downPayment}
            onChange={(e) => setField("downPayment", e.target.value.replace(/[^\d.]/g, ""))}
          />
          {form.authMode === "NACH" && Number(form.downPayment || 0) > 0 && (
            <p className="mt-1 text-xs text-danger font-medium">
              eNACH does not allow any advance collection. Please set to ₹0 or select UPI Autopay.
            </p>
          )}
          {form.authMode === "UPI_AUTO_PAY" && Number(form.downPayment || 0) > 15000 && (
            <p className="mt-1 text-xs text-danger font-medium">
              UPI advance amount cannot exceed ₹15,000 (NPCI limit). Collect excess offline.
            </p>
          )}
          {Number(form.downPayment || 0) > 0 && isDateToday(form.startDate) && (
            <p className="mt-1 text-xs text-amber-600 font-medium">
              Advance amount is not allowed if mandate starts today. Select a future start date.
            </p>
          )}
        </FieldWrapper>

        <FieldWrapper
          label="Interest Rate"
          required
          error={errors.interestRate}
          htmlFor="interestRate"
          helper="Annual flat interest rate"
        >
          <div className="flex w-full">
            <input
              id="interestRate"
              inputMode="decimal"
              placeholder="e.g. 12"
              value={form.interestRate}
              onChange={(e) => setField("interestRate", e.target.value.replace(/[^\d.]/g, ""))}
              className={`h-[42px] w-full rounded-l-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
                errors.interestRate
                  ? "border-danger focus:ring-danger/30"
                  : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
              }`}
            />
            <span className="inline-flex h-[42px] shrink-0 items-center rounded-r-lg border border-l-0 border-slate-300 bg-page px-3.5 text-sm font-medium text-ink-secondary">
              % p.a.
            </span>
          </div>
        </FieldWrapper>

        <Select
          label="Tenure"
          required
          placeholder="Select tenure"
          options={TENURE_OPTIONS}
          error={!isOtherTenure ? errors.tenure : undefined}
          id="tenure"
          value={isOtherTenure ? "OTHER" : (PRESET_TENURES.includes(form.tenure) ? form.tenure : "")}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "OTHER") {
              setIsOtherTenure(true);
              setField("tenure", "");
            } else {
              setIsOtherTenure(false);
              setField("tenure", val);
            }
          }}
        />

        {isOtherTenure && (
          <FieldWrapper
            label="Custom Tenure (in Months)"
            required
            error={errors.tenure}
            htmlFor="customTenure"
            helper="Enter total duration in months (e.g. 5, 8, 15, 48)"
          >
            <div className="flex w-full">
              <input
                id="customTenure"
                type="text"
                inputMode="numeric"
                placeholder="Enter number of months (e.g. 5)"
                value={form.tenure}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\D/g, "").slice(0, 3);
                  setField("tenure", cleaned);
                }}
                className={`h-[42px] w-full rounded-l-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
                  errors.tenure
                    ? "border-danger focus:ring-danger/30"
                    : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
                }`}
              />
              <span className="inline-flex h-[42px] shrink-0 items-center rounded-r-lg border border-l-0 border-slate-300 bg-page px-3.5 text-sm font-medium text-ink-secondary">
                Months
              </span>
            </div>
          </FieldWrapper>
        )}
      </div>

      {/* EMI calculation summary */}
      <div className="mt-6 rounded-[10px] border border-line bg-[#F8FAFC] p-5">
        <p className="text-sm font-semibold text-ink">EMI Calculation</p>
        <dl className="mt-1 divide-y divide-slate-200">
          <DefRow label="Financed Amount" value={emi ? formatMoney(emi.financed) : emdash} muted={!emi} />
          <DefRow label="Total Interest" value={emi ? formatMoney(emi.interest) : emdash} muted={!emi} />
          <DefRow label="Total Payable" value={emi ? formatMoney(emi.payable) : emdash} muted={!emi} />
          <div className="border-t-2 border-slate-200">
            <div className="flex items-center justify-between gap-4 py-3">
              <span className="text-sm font-semibold text-ink">Monthly EMI</span>
              <span className="text-xl font-bold text-primary-500">
                {emi ? formatMoney(emi.emi) : emdash}
              </span>
            </div>
          </div>
        </dl>
        <p className="mt-1 text-xs text-ink-muted">
          {emi && form.tenure
            ? `${form.tenure} monthly installments of ${formatMoney(emi.emi)}`
            : "Fill in product price, down payment, interest rate and tenure to see the EMI."}
        </p>
      </div>

      {/* Mandate fields */}
      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <FieldWrapper label="Authentication Mode" required error={errors.authMode}>
            <div id="authMode" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {MODES.map((m) => {
                const selected = form.authMode === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setField("authMode", m.key)}
                    className={`relative rounded-lg border px-4 py-4 text-left transition-colors cursor-pointer ${
                      selected
                        ? "border-primary-500 bg-[#EFF6FF]"
                        : "border-slate-300 bg-white hover:border-slate-400"
                    }`}
                  >
                    {selected && (
                      <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500">
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      </span>
                    )}
                    <m.icon className="h-6 w-6 text-primary-500" />
                    <p className="mt-2 text-sm font-medium text-ink">{m.title}</p>
                    <p className="mt-1 text-xs leading-snug text-ink-secondary">{m.sub}</p>
                  </button>
                );
              })}
            </div>
          </FieldWrapper>
          {form.authMode === "UPI_AUTO_PAY" && (
            <div className="mt-4">
              <Input
                id="upiId"
                label="Customer UPI ID"
                required
                value={form.upiId}
                onChange={(e) => setField("upiId", e.target.value)}
                error={errors.upiId}
                placeholder="customer@okaxis"
                helper="The UPI ID the customer will authorise the autopay mandate with"
              />
            </div>
          )}
        </div>

        <FieldWrapper
          label="Mandate Amount"
          required
          error={errors.amount}
          htmlFor="amount"
          helper="This amount will be debited every month"
        >
          <PrefixInput
            prefix="₹"
            id="amount"
            inputMode="numeric"
            placeholder="Auto-filled from EMI"
            error={!!errors.amount}
            readOnly
            value={form.amount}
            className="bg-[#F8FAFC]"
            onChange={() => undefined}
          />
        </FieldWrapper>

        <FieldWrapper
          label="Maximum Amount"
          required
          error={errors.maxAmount}
          htmlFor="maxAmount"
          helper="Upper limit per debit — keep a small buffer above the EMI"
        >
          <PrefixInput
            prefix="₹"
            id="maxAmount"
            inputMode="numeric"
            placeholder="Enter maximum amount"
            error={!!errors.maxAmount}
            value={form.maxAmount}
            onChange={(e) => setField("maxAmount", e.target.value.replace(/[^\d.]/g, ""))}
          />
        </FieldWrapper>

        <Select
          label="Frequency"
          required
          disabled
          options={FREQUENCY_OPTIONS}
          helper="EMI collections are monthly"
          id="frequency"
          value={form.frequency || "Monthly"}
          onChange={(e) => setField("frequency", e.target.value)}
        />

        <Select
          label="Purpose"
          required
          disabled
          options={PURPOSE_OPTIONS}
          id="purpose"
          value={form.purpose || "EMI Collection"}
          onChange={(e) => setField("purpose", e.target.value)}
        />

        <FieldWrapper
          label="First Installment Date"
          required
          error={errors.startDate}
          htmlFor="startDate"
          helper="Debits will be attempted on this date every month"
        >
          <DatePicker
            id="startDate"
            value={form.startDate}
            placeholder="Select first installment date"
            onChange={(v) => setField("startDate", v)}
          />
        </FieldWrapper>

        <FieldWrapper label="End Date" required error={errors.endDate} htmlFor="endDate">
          <div className="pointer-events-none">
            <DatePicker
              id="endDate"
              value={form.endDate}
              placeholder="Auto-calculated from tenure"
              onChange={(v) => setField("endDate", v)}
            />
          </div>
        </FieldWrapper>

        <div className="md:col-span-2">
          <Textarea
            label="Remarks"
            rows={2}
            maxLength={200}
            placeholder="Any additional notes"
            helper={`${form.remarks.length}/200 characters`}
            value={form.remarks}
            onChange={(e) => setField("remarks", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}